import { ReactNode } from 'react'
import { ChangelogEntry } from './componentsV2/RevisionHistory/RevisionHistorySection'

export enum MedicationStockReason {
  INITIAL_STOCK = 'INITIAL_STOCK',
  RESTOCK = 'RESTOCK',
  MED_ADMIN = 'MED_ADMIN',
  DISPOSAL = 'DISPOSAL',
  STOCK_COUNT = 'STOCK_COUNT',
  DISCONTINUED = 'DISCONTINUED',
  NOT_ADMINISTERED = 'NOT_ADMINISTERED',
  RECONCILIATION = 'RECONCILIATION'
}

export enum DiscrepancyStatus {
  PENDING = 'PENDING',
  RESOLVED = 'Resolved',
  INVESTIGATING = 'INVESTIGATING'
}

export enum AdministrationStatus {
  SCHEDULED = 'SCHEDULED',
  GIVEN = 'GIVEN',
  MISSED = 'MISSED',
  REFUSED = 'REFUSED',
  OVERDUE = 'OVERDUE',
  PRN_AVAILABLE = 'PRN_AVAILABLE',
  OTHER = 'OTHER',
  ABSENT = 'ABSENT',
  VERBAL_PROMPT = 'VERBAL_PROMPT',
  ALREADY_TAKEN = 'ALREADY_TAKEN',
  LEFT_OUT = 'LEFT_OUT',
  REFUSED_DESTROYED = 'REFUSED_DESTROYED',
  NAUSEA_VOMITING = 'NAUSEA_VOMITING',
  NOT_NEEDED = 'NOT_NEEDED',
  PAUSED = 'PAUSED',
  DISCONTINUED = 'DISCONTINUED',
  UNKNOWN = 'UNKNOWN'
}

export type MedicationAction = {
  code?: string
  icon?: ReactNode
  translationKey: string
}

export type DisposalData = {
  description: string
  disposedStock: number
  newStock: number
  hasDiscrepancy: boolean
  discrepancyReason: string
  actionedBy: string
  actionTime?: any // dayjs object
  disposalMethod: string
  additionalDetails: boolean
  media?: File[]
  mediaAttachmentKeysToRemove?: string[]
}

export type StockCheckData = {
  description: string
  stockCount: number
  adjustment: number
  additionalDetails: boolean
  isExpectedStockAccurate: boolean
  resolveDiscrepancy?: string | null
  discrepancyReason?: string
  media?: File[]
  mediaAttachmentKeysToRemove?: string[]
}

export type MediaAttachment = {
  url: string
  key: string
  bucket: string
  contentType: string
  size: number
  uploadedAt: string
  uploadedBy: string
  _id?: string
}

export interface StockRecordWitness {
  userId: string
  witnessedAt: string
}

export interface StockRecordDiscrepancy {
  amount: number
  status: DiscrepancyStatus
  carerReason?: string
  reason?: string
}

export interface StockRecordListItem {
  id: string
  date: string
  recordedBy: string
  action: MedicationStockReason
  notes?: string | string[]
  expectedStock: number
  stockCount: number
  quantityChange: number
  unit: string
  witnessed: boolean
  witnesses?: StockRecordWitness[]
  discrepancy?: StockRecordDiscrepancy
}

export type ResidentMedication = {
  patternId: string
  residentMedicationId: string
  medicationName: string
  dose: string
  route: string
  currentStockLevel: number
  unit: string
  stockAlertLevel: number
  isBelowThreshold: boolean
  lastStockRecordDate: string
  lastStockCountDate: string
  daysSinceLastStockCount: Record<string, any>
  controlledDrug: boolean
  secondSignatureRequired: boolean
  discontinued: Record<string, any>
  status: string
  createdAt?: string
  updatedAt?: string
  createdByName?: string
  updatedByName?: string
  lastStockRecordedByName?: string
}

export type StockRecord = {
  id: string
  date: string
  actionTime?: string
  administrationStatus?: AdministrationStatus
  type?: string
  recordedBy: {
    id: string
    name: string
    picture?: string
  }
  receivedBy?: {
    id: string
    name: {
      first: string
      last: string
    }
    picture?: string
  }
  receivedFrom?: string
  action: MedicationStockReason
  notes?: string | string[]
  expectedStock?: number
  stockCount?: number
  quantityChange: number
  unit: string
  witnessed: boolean
  witnesses?: StockRecordWitness[]
  hasDiscrepancy?: boolean
  discrepancy?: StockRecordDiscrepancy
  resident?: {
    id: string
    name: {
      first: string
      last: string
    }
    preferredName?: string
    picture?: string
  }
  disposedBy?: {
    id: string
    name: {
      first: string
      last: string
    }
  }
  disposalMethod?: string
  resultingStockLevel?: number
  previousStockLevel?: number
  mediaAttachments?: MediaAttachment[]
  changelog?: ChangelogEntry[]
  administration?: {
    _id: string
    createdAt: string
    updatedAt: string
    recordedBy: {
      id: string
      name: {
        first: string
        last: string
      }
      picture?: string
    }
    updatedBy: string
    homeId: string
    status: string
    residentId: string
    patternId: string
    medicationId: string
    residentMedicationId: string
    roundTimingId: string
    startTime: string
    endTime: string
    dose: string
    customDose: string
    doseQuantity: number
    prn: boolean
    administrationStatus: AdministrationStatus | string
    administeredTime: string
    reasonCode?: string | null
    note?: string | null
    deleted: boolean
    emarRoundKey: string
    roundOverdue: boolean
    roundType: string
    resident?: {
      id: string
      name: {
        first: string
        last: string
      }
      firstName?: string
      picture?: string
    }
    createdByUser?: {
      id: string
      name: {
        first: string
        last: string
      }
      email?: string
      picture?: string
      [key: string]: any
    }
    [key: string]: any
  }
}

export type DiscrepancyRecord = {
  _id: string
  date: string
  actionTime: string
  actionedDate?: string
  resident: {
    id: string
    name: {
      first: string
      last: string
    }
    preferredName?: string
    picture?: string
  }
  medicationName: string
  prn: boolean
  controlledDrug: boolean
  resultingStockLevel: number
  previousStockLevel: number
  quantityChange: number
  unit: string
  reason: string
  recordedBy: {
    id: string
    name: string
    picture?: string | null
  }
  recordedByUser?: {
    id: string
    name: {
      first: string
      last: string
    }
    email?: string
    picture?: string
    [key: string]: any
  }
  witnesses?: any[]
  notes?: string | string[]
  systemDescription?: string
  discrepancy: {
    amount: number
    status: string
    reason?: string
    _id?: string
  }
  mediaAttachments?: MediaAttachment[]
  metaData?: {
    description?: string
    [key: string]: any
  }
  changelog?: ChangelogEntry[]
  [key: string]: any
}

export type ChartDataPoint = {
  date: string
  stockLevel: number
  recordId?: string
  reason?: string
  expectedStockLevel?: number
  // Marker configuration (already calculated in backend)
  marker?: {
    symbol: string | null
    height?: number
    width?: number
    color: string
    lineColor: string
  }
  // Tooltip data (optional - can be derived from record if needed)
  record?: StockRecord
}

export type StockRecordsResponse = {
  records: StockRecord[]
  chartData: ChartDataPoint[]
  total: number
  hasActivity: boolean
  suggestedActivityDate?: string
}

export type ResidentMedicationsResponse = {
  scheduledMedications: ResidentMedication[]
  prnMedications: ResidentMedication[]
  discontinuedMedications: ResidentMedication[]
}

export type AddStockData = {
  description: string
  receivedStock: number
  newStock: number
  isStockAccurate: boolean
  receivedBy: string
  actionTime?: any // dayjs object
  resolveDiscrepancy: string | null
  discrepancyReason: string
  receivedFrom: string
  witnessedBy: string
  witnessPin: string
  additionalDetails: boolean
  media?: File[]
}

export type CurrentStockResponse = {
  currentStockLevel: number
  unit: string
  lastStockRecordDate: string
  resident?: {
    id: string
    name: string
    preferredName?: string
    image?: string
  }
  witnesses?: Array<{
    userId: string
    witnessedAt: string
    addedBy: string
    name: string
  }>
  witnessedBy?: any
  notes?: string | string[]
  disposalMethod?: string
  sourceRecordId?: string
  stockAlertLevel?: number
  stockQuantityType?: string
  deliveryId?: string
  relatedRecordId?: any
  discrepancy?: any
  changelog?: string[]
  mediaAttachments?: Array<{
    url: string
    key: string
    bucket: string
    contentType: string
    size: number
    uploadedAt: string
    uploadedBy: string
  }>
}

export type MedicationTableData = {
  name: string
  currentStock: number
  startDate: string
  endDate: string
  isControlledDrug: boolean
  lowStock?: boolean
  outOfStock?: boolean
  stockUnit?: string
}

export type MarChartDateStatus = {
  date: string
  dayOfWeek: string
  status: string
  symbol?: string
  timingTime?: string
  timingName?: string
  roundTimingId?: string
  administeredTime?: string
  doseQuantity?: number
  doseUnit?: string
  recordedBy?: {
    id: string
    name: {
      first: string
      last: string
    }
    picture?: string | null
  }
  details?: string
  reasonCode?: string
  note?: string
}

export type MarChartRoundTiming = {
  roundTimingId: string
  timingName: string
  timingTime: string
  dose: string
  doseUnit?: string
  dateStatuses: MarChartDateStatus[]
}

export type MarChartMedication = {
  residentMedicationId: string
  patternId: string
  medicationId: string
  medicationName: string
  currentStock: number
  stockUnit?: string
  startDate: string
  dose: string | object
  doseUnit?: string
  controlledDrug: boolean
  secondSignatureRequired: boolean
  variableDose: boolean
  blisterPack: boolean
  quantityType?: string
  homeRemedy: boolean
  overTheCounter: boolean
  hasStockTracking: boolean
  prn: boolean
  roundTimings: MarChartRoundTiming[]
}

export type MarChartData = {
  medications: MarChartMedication[]
  dateRange: {
    startDate: string
    endDate: string
  }
}

export type MedicationSchedulePatternDose = {
  dose: number
  customDose: number | null
  altDose: number | null
  unit: string
  roundTimingId: string
  trackStock: boolean
}

export type MedicationSchedulePattern = {
  status: string
  version: number
  _id: string
  blisterPack: boolean
  controlledDrug: boolean
  createdBy: string
  createdOn: string
  deleted: boolean
  doses: MedicationSchedulePatternDose[]
  frequency: string
  frequencyError: string | null
  homeId: string
  homeRemedy: boolean
  interval: number
  medicationId: string
  medicationName: string
  overTheCounter: boolean
  prescriptionCount: number
  prn: boolean
  quantityType: string
  residentId: string
  residentMedicationId: string
  route: string
  secondSignatureRequired: boolean
  startDate: string
  stockAlertLevel: number
  stockQuantityType: string
  timezone: string
  variableDose: boolean
  createdAt: string
  updatedAt: string
}

export type MedicationTableRow = {
  key: string
  medication: MedicationTableData
  hour?: string
  dose: string
  residentMedicationId: string
  [dateKey: string]: any
}

export type AdministrationRecord = {
  type: 'administration'
  id: string
  date: string
  actionTime?: string
  administration: {
    _id: string
    createdAt: string
    updatedAt: string
    recordedBy: {
      id: string
      name: {
        first: string
        last: string
      }
      picture?: string
    }
    updatedBy: string
    homeId: string
    status: string
    residentId: string
    patternId: string
    medicationId: string
    residentMedicationId: string
    roundTimingId: string
    startTime: string
    endTime: string
    dose: string
    customDose: string
    doseQuantity: number
    prn: boolean
    administrationStatus: AdministrationStatus | string
    administeredTime: string
    reasonCode?: string | null
    note?: string | null
    deleted: boolean
    emarRoundKey: string
    roundOverdue: boolean
    roundType: string
    resident?: {
      id: string
      name: {
        first: string
        last: string
      }
      firstName?: string
      picture?: string
    }
    createdByUser?: {
      id: string
      name: {
        first: string
        last: string
      }
      email?: string
      picture?: string
      [key: string]: any
    }
    [key: string]: any
  }
}
